import { getAirlines, getNonRevAirlines, getExcludedAirlines, excludeIncludeAirlines } from 'apis/settingsServices';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useToastStore } from '../../store/toastStore'; // Import the toaster store
import { useGlobalLoader } from '../../../src/contexts/GlobalLoaderContext'
import { GroupBase, StylesConfig } from 'react-select';

interface Airline {
  airlineId: number;
  id: number;
  airlineName: string;
  logo: string;
}

interface AirlineOption {
  logo: string;
  value: number;
  label: string;
}

const FlightSearch: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [nonRevAirlines, setNonRevAirlines] = useState<Airline[]>([]);
  const [excludedAirlines, setExcludedAirlines] = useState<AirlineOption[]>([]);
  const [airlineOptions, setAirlineOptions] = useState<AirlineOption[]>([]);
  const [selectedAirlines, setSelectedAirlines] = useState<AirlineOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { showToast } = useToastStore.getState(); // Get the showToast function from the store

  const customStyles: StylesConfig<AirlineOption, true, GroupBase<AirlineOption>> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#F5F9FE',
      border: 'none',
      height: 'fit-content',
      minHeight: '50px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#F5F9FE',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#DCEEFF'
        : state.isFocused
          ? '#EAF4FF'
          : '#F5F9FE',
      color: '#000',
    }),
  };
  // Fetch Non-Rev airlines
  useEffect(() => {
    const fetchAirlines = async () => {
      showLoader();
      try {
        const response = await getNonRevAirlines();
        setNonRevAirlines(response);
        console.log("Fetched airlines:", response);
      } catch (error) {
        console.error('Failed to fetch airlines', error);
        //showToast('Failed to fetch Non-Rev airlines. Please try again.', 'error'); // Show error toaster
      }
      finally {
        hideLoader();
      }
    };

    fetchAirlines();
  }, [showToast]);

  // Fetch Excluded Airlines (Runs on mount & after save)
  const fetchExcludedAirlines = async () => {
    try {
      const response = await getExcludedAirlines();
      if (response && Array.isArray(response)) {
        const formatted = response.map((airline: Airline) => ({
          value: airline.id,
          label: airline.airlineName,
          logo: airline.logo
        }));
        setExcludedAirlines(formatted);
      }
    } catch (error) {
      console.error('Failed to fetch excluded airlines', error);
      //showToast('Failed to fetch excluded airlines. Please try again.', 'error'); // Show error toaster
    }
  };

  useEffect(() => {
    fetchExcludedAirlines();
  }, [showToast]);

  // Fetch airline options for search
  const fetchAirlineOptions = async (inputValue: string) => {
    try {
      const response = await getAirlines(inputValue);
      if (response && Array.isArray(response)) {
        const options = response.map((airline: Airline) => ({
          value: airline.id,
          label: airline.airlineName,
          logo: airline.logo,
        }));
        setAirlineOptions(options);
      }
    } catch (error) {
      console.error("Error fetching airlines", error);
      showToast("Error fetching airline options. Please try again.", "error"); // Show error toaster
    }
  };

  const handleSearchChange = (inputValue: string) => {
    fetchAirlineOptions(inputValue);
  };

  const handleSelectChange = (selected: readonly AirlineOption[]) => {
    setSelectedAirlines([...selected]); // Store selection but don't update UI yet
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple clicks
    if (!isDirty) return;

    if (selectedAirlines.length === 0) {
      showToast('Please select at least one airline to exclude.', 'error', 3000);
      return;
    }

    setIsSaving(true);
    try {
      const selectedIds = selectedAirlines.map((airline) =>
        Number(airline.value)
      );
      await excludeIncludeAirlines(selectedIds, true);
      console.log("Excluded airlines saved:", selectedIds);

            // Show success toaster

      showToast('Excluded airlines saved successfully!', 'success', 3000);
      await fetchExcludedAirlines();
      setSelectedAirlines([]);
      setIsDirty(false);

    } catch (error) {
      console.error('Failed to save excluded airlines', error);
      showToast('Failed to save excluded airlines. Please try again.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  };


  const handleRemoveAirline = async (id: number) => {
    try {
      await excludeIncludeAirlines([id], false); // Ensure id is a number

      // Update UI immediately
      setExcludedAirlines((prevExcluded) => prevExcluded.filter((airline) => airline.value !== id));

      showToast('Airline removed from exclusion list.', 'success');
      setIsDirty(true);

    } catch (error) {
      console.error("Failed to include airline", error);
      showToast("Failed to include airline. Please try again.", "error");
    }
  };

  const handleCancel = async () => {
    setIsSaving(true);
    try {
      const allIds = excludedAirlines.map((airline) => airline.value);
      if (allIds.length > 0) {
        await excludeIncludeAirlines(allIds, false);
      }
      setExcludedAirlines([]);
      setSelectedAirlines([]);
      setIsDirty(false);
      showToast('All excluded airlines have been removed.', 'success', 3000);
    } catch (error) {
      showToast('Failed to remove all excluded airlines. Please try again.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  };

  
  return (
    <div className="mt-4 sm:ml-64 flight-search">
      <div className="bg-white p-6 rounded-xl">
        <p className="text-grease-100 text-md font-medium mb-6">
          Airlines You can Non-Rev with
        </p>
        <div className="grid lg:grid-cols-5 sm:grid-cols-3 gap-6">
          {nonRevAirlines.map((nonRevAirline) => (
            <div
              key={nonRevAirline?.id}
              className="px-4 py-2 flex items-center gap-4 border border-blue-100 rounded-xl"
            >
              <img
                className="w-10 h-10"
                src={`/assets/logos/${nonRevAirline?.airlineId}.jpg`}
                alt={nonRevAirline?.airlineName}
              />
              <p className="text-sm text-gray-700 font-medium">
                {nonRevAirline?.airlineName}
              </p>
            </div>
          ))}
        </div>

        <p className="text-grease-100 text-md font-medium my-6">Excluded Airlines</p>
        <div className=" lg:grid-cols-3 sm:grid-cols-3 gap-6">
          <div className="relative row-start-1 col-span-2 w-10/12 lg:w-4/12   ">
            <Select
              options={airlineOptions}
              onInputChange={handleSearchChange}
              onMenuOpen={() => fetchAirlineOptions('')} // Fetch options when menu opens
              value={selectedAirlines}
              onChange={handleSelectChange}
              placeholder="Search airlines..."
              isClearable
              isMulti
              className="basic-multi-select "
              classNamePrefix="select"
              styles={customStyles}
            />
          </div>
          <div className="mt-5 flex flex-wrap  gap-4 col-span-2 ">
            {excludedAirlines.map((airline) => (
              <div key={airline.value} className=" row-start-2 px-4 py-2 flex items-center justify-between gap-4 border border-blue-100 rounded-xl">
                <div className="flex items-center gap-4">
                  {/* <img className="w-10 h-10" src={airline?.logo} alt={airline?.label} /> */}
                  <p className="text-sm text-gray-700 font-medium">{airline?.label}</p>
                </div>
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 cursor-pointer"
                  onClick={() => handleRemoveAirline(airline.value)}
                >
                  <i className="st-close text-lightgray-150 text-xs"></i>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-4">
          <button
            className="p-4 w-32 rounded-lg text-lightgray-200 text-center bg-lightgray-100 text-xs mt-6"
            onClick={handleCancel}
            // disabled={!isDirty || isSaving}    
                  >
            REMOVE ALL
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className={`p-4 w-32 rounded-lg text-xs mt-6 text-white bg-[#FFBC00] ${isDirty && !isSaving ? 'shadow-button cursor-pointer' : 'opacity-50 cursor-not-allowed'
              }`}
          >
            {isSaving ? 'SAVING...' : 'SAVE'}
          </button>

        </div>
      </div>
    </div>
  );
};

export default FlightSearch;
